import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import type { ApiCatalogListItem } from '@chatsdet/types';
import {
  BulkActionBar,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Flex,
  LoadingOrbit,
  Menu,
  MenuItem,
  MenuTrigger,
  Page,
  PageHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Spinner,
  Text,
  Tooltip,
  TooltipTrigger,
  useToast,
  DocsAnnotation,
} from '@chatsdet/ui';
import {
  Code,
  DownloadSimple,
  Plus,
  UploadSimple,
  SlidersHorizontal,
  ArrowsClockwise,
  Trash,
  LinkSimple,
} from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import {
  useApiCatalogDomainMetadata,
  useApiRequestDetail,
  useApiRequestList,
  useBulkDeleteApiRequests,
  useExportToPostman,
  useListSources,
  useRefreshSource,
  useDeleteSource,
  type ApiRequestListQuery,
} from '../../features/api-catalog/api';
import type { DateRangeValue } from '../../utils/dateRange';
import { parseIsoToDateRange, toInclusiveIsoRange } from '../../utils/dateRange';
import { ApiRequestDrawer } from './api-catalog/ApiRequestDrawer';
import {
  ApiCatalogFilterBar,
  type ApiCatalogFiltersSearchParams,
} from './api-catalog/ApiCatalogFilterBar';
import { getApiCatalogEmptyStateVariant } from './api-catalog/emptyState';
import { ImportPostmanDialog } from '../../features/api-catalog/components/ImportPostmanDialog';
import { ImportUrlDialog } from '../../features/api-catalog/components/ImportUrlDialog';
import { CreateEndpointDialog } from '../../features/api-catalog/components/CreateEndpointDialog';
import {
  MethodBadge,
  StatusDots,
  formatRelativeTime,
} from '../../features/api-catalog/components/shared';
import { useOrgCredentials } from '../../hooks/useOrgCredentials';

const PAGE_SIZE = 25;
const SHOW_DOMAIN_COLUMN_KEY = 'apiCatalog.columns.domain.visible';

const formatRecentStatuses = (statusCodes: Array<number | null>): string => {
  if (!statusCodes.length) return 'No recent requests captured';
  return statusCodes.map((code) => (typeof code === 'number' ? String(code) : '—')).join(' · ');
};

const columnHeader = (label: string, tooltip?: string) => {
  if (tooltip) {
    return () => (
      <TooltipTrigger delay={100} offset={8}>
        <Text size="sm" weight="medium" className="cursor-default">
          {label}
        </Text>
        <Tooltip>{tooltip}</Tooltip>
      </TooltipTrigger>
    );
  }
  return () => (
    <Text size="sm" weight="medium">
      {label}
    </Text>
  );
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const buildListColumns = (
  showDomainColumn: boolean
): ColumnDef<StockFeatures, ApiCatalogListItem>[] => {
  const columns: ColumnDef<StockFeatures, ApiCatalogListItem>[] = [
    {
      header: columnHeader('Endpoint'),
      id: 'endpoint',
      cell: ({ row }) => (
        <Flex align="center" gap="xs">
          <MethodBadge method={row.original.method} />
          <Text size="sm" className="font-mono truncate" title={row.original.pattern}>
            {row.original.pattern}
          </Text>
        </Flex>
      ),
      meta: { flex: 1.8 },
    },
    ...(showDomainColumn
      ? ([
          {
            header: columnHeader('Domain'),
            accessorKey: 'domain',
            cell: ({ row }) => (
              <Text
                size="sm"
                className="truncate max-w-[220px]"
                title={row.original.domain ?? undefined}
              >
                {row.original.domain ?? '—'}
              </Text>
            ),
            meta: { flex: 0.4 },
          },
        ] satisfies ColumnDef<StockFeatures, ApiCatalogListItem>[])
      : []),
    {
      header: columnHeader('Health', 'Based on the last 5 observed status codes.'),
      accessorKey: 'health',
      enableSorting: false,
      size: 100,
      cell: ({ row }) => (
        <TooltipTrigger delay={100} offset={8}>
          <div className="cursor-default">
            <StatusDots statusCodes={row.original.recentStatusCodes} />
          </div>
          <Tooltip>Last 5: {formatRecentStatuses(row.original.recentStatusCodes)}</Tooltip>
        </TooltipTrigger>
      ),
      meta: { flex: 0.2 },
    },
    {
      header: columnHeader('Last Seen'),
      accessorKey: 'lastSeenAt',
      cell: ({ row }) => (
        <TooltipTrigger delay={100} offset={8}>
          <Text size="sm" className="cursor-default">
            {formatRelativeTime(row.original.lastSeenAt)}
          </Text>
          <Tooltip>{formatDate(row.original.lastSeenAt)}</Tooltip>
        </TooltipTrigger>
      ),
      meta: { flex: 0.25 },
    },
    {
      header: columnHeader('Requests', 'Total number of observed requests across all traces.'),
      accessorKey: 'occurrenceCount',
      size: 100,
      cell: ({ row }) => <Text size="sm">{row.original.occurrenceCount}</Text>,
      meta: { flex: 0.2 },
    },
  ];

  return columns;
};

type ApiCatalogSearchParams = {
  requestId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  domains?: string[];
  methods?: string[];
  statuses?: Array<'2xx' | '4xx' | '5xx'>;
  credentialIds?: string[];
  lastSeenStart?: string;
  lastSeenEnd?: string;
};

export const ApiCatalog: React.FC = () => {
  useBreadcrumbs([{ label: 'Home' }, { label: 'API Catalog' }]);

  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/api-requests' }) as ApiCatalogSearchParams;

  const updateSearch = React.useCallback(
    (updates: Partial<ApiCatalogFiltersSearchParams> & Pick<ApiCatalogSearchParams, 'requestId'>) =>
      navigate({
        to: '/api-requests',
        search: (prev: Record<string, unknown>) => {
          const prevParams = prev as ApiCatalogSearchParams;
          const merged = { ...prevParams, ...updates };

          const next: ApiCatalogSearchParams = {
            ...merged,
            page: Math.max(merged.page ?? 1, 1),
            pageSize: Math.max(merged.pageSize ?? PAGE_SIZE, 1),
          };

          if (!next.requestId) delete next.requestId;
          if (!next.search) delete next.search;
          if (!next.domains || next.domains.length === 0) delete next.domains;
          if (!next.methods || next.methods.length === 0) delete next.methods;
          if (!next.statuses || next.statuses.length === 0) delete next.statuses;
          if (!next.credentialIds || next.credentialIds.length === 0) delete next.credentialIds;
          if (!next.lastSeenStart || !next.lastSeenEnd) {
            delete next.lastSeenStart;
            delete next.lastSeenEnd;
          }

          return next;
        },
      }),
    [navigate]
  );

  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? PAGE_SIZE;
  const searchValue = searchParams.search ?? '';
  const [localSearch, setLocalSearch] = React.useState(searchValue);
  const selectedRequestId = searchParams.requestId ?? null;

  // Debounce search input (300ms)
  React.useEffect(() => {
    const trimmed = localSearch.trim();
    if (trimmed === searchValue) return;
    const timer = setTimeout(() => {
      updateSearch({ search: trimmed || undefined, page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local search when URL changes externally
  React.useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);
  const drawerOpen = Boolean(selectedRequestId);

  const domainFilter = searchParams.domains ?? [];
  const methodFilter = searchParams.methods ?? [];
  const statusFilter = searchParams.statuses ?? [];
  const credentialFilter = searchParams.credentialIds ?? [];
  const lastSeenRange: DateRangeValue = React.useMemo(
    () => parseIsoToDateRange(searchParams.lastSeenStart, searchParams.lastSeenEnd),
    [searchParams.lastSeenEnd, searchParams.lastSeenStart]
  );

  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = React.useState(false);
  const [isColumnsDialogOpen, setIsColumnsDialogOpen] = React.useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false);
  const [isUrlImportDialogOpen, setIsUrlImportDialogOpen] = React.useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [exportDomain, setExportDomain] = React.useState<string | null>(null);
  const [exportIncludeExamples, setExportIncludeExamples] = React.useState(true);

  const { toast } = useToast();
  const exportMutation = useExportToPostman();
  const sourcesQuery = useListSources();
  const refreshMutation = useRefreshSource();
  const deleteSourceMutation = useDeleteSource();

  const handleExport = React.useCallback(() => {
    if (!exportDomain) return;

    exportMutation.mutate(
      { domain: exportDomain, includeExamples: exportIncludeExamples },
      {
        onSuccess: () => {
          toast.success(`Exported ${exportDomain} collection`);
          setIsExportDialogOpen(false);
          setExportDomain(null);
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Export failed');
        },
      }
    );
  }, [exportDomain, exportIncludeExamples, exportMutation, toast]);

  const [showDomainColumn, setShowDomainColumn] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_DOMAIN_COLUMN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(SHOW_DOMAIN_COLUMN_KEY, showDomainColumn ? 'true' : 'false');
    } catch {
      // ignore persistence failures (e.g., blocked storage)
    }
  }, [showDomainColumn]);

  const domainMetadataQuery = useApiCatalogDomainMetadata();
  const credentialsQuery = useOrgCredentials();

  const domainOptions = React.useMemo(
    () =>
      (domainMetadataQuery.data?.domains ?? [])
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .map((domain) => ({ id: domain, label: domain })),
    [domainMetadataQuery.data?.domains]
  );

  const credentialOptions = React.useMemo(
    () => credentialsQuery.credentialOptions.map((c) => ({ id: c.id, label: c.label })),
    [credentialsQuery.credentialOptions]
  );

  const lastSeenIsoRange = React.useMemo(() => toInclusiveIsoRange(lastSeenRange), [lastSeenRange]);

  const queryParams: ApiRequestListQuery = React.useMemo(
    () => ({
      page,
      pageSize,
      search: searchValue.trim() ? searchValue : undefined,
      domains: domainFilter.length > 0 ? domainFilter : undefined,
      methods: methodFilter.length > 0 ? methodFilter : undefined,
      statuses: statusFilter.length > 0 ? statusFilter : undefined,
      credentialIds: credentialFilter.length > 0 ? credentialFilter : undefined,
      lastSeenStart: lastSeenIsoRange.start ?? undefined,
      lastSeenEnd: lastSeenIsoRange.end ?? undefined,
    }),
    [
      credentialFilter,
      domainFilter,
      lastSeenIsoRange.end,
      lastSeenIsoRange.start,
      methodFilter,
      page,
      pageSize,
      searchValue,
      statusFilter,
    ]
  );

  const listQuery = useApiRequestList(queryParams);
  const detailQuery = useApiRequestDetail(drawerOpen ? selectedRequestId : null);
  const bulkDeleteMutation = useBulkDeleteApiRequests();

  const listItems = listQuery.data?.data ?? [];
  const pagination = listQuery.data?.pagination;

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);

  // Clear selection on page change
  const prevPage = React.useRef(page);
  React.useEffect(() => {
    if (prevPage.current !== page) {
      setRowSelection({});
      prevPage.current = page;
    }
  }, [page]);

  const selectedIds = React.useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );

  const selectedCount = selectedIds.length;

  const handleBulkDelete = React.useCallback(() => {
    if (selectedIds.length === 0) return;
    bulkDeleteMutation.mutate(selectedIds, {
      onSuccess: (result) => {
        toast.success(`Deleted ${result.deleted} endpoint${result.deleted === 1 ? '' : 's'}`);
        setRowSelection({});
        setIsDeleteConfirmOpen(false);
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Delete failed');
        setIsDeleteConfirmOpen(false);
      },
    });
  }, [selectedIds, bulkDeleteMutation, toast]);

  const selectedListItem = React.useMemo(
    () =>
      selectedRequestId ? (listItems.find((item) => item.id === selectedRequestId) ?? null) : null,
    [listItems, selectedRequestId]
  );

  const tableColumns = React.useMemo(() => buildListColumns(showDomainColumn), [showDomainColumn]);

  const hasActiveFilters =
    domainFilter.length > 0 ||
    methodFilter.length > 0 ||
    statusFilter.length > 0 ||
    credentialFilter.length > 0 ||
    Boolean(lastSeenRange?.start && lastSeenRange?.end);

  const handleLastSeenChange = React.useCallback(
    (range: DateRangeValue) => {
      const iso = toInclusiveIsoRange(range);
      if (!iso.start || !iso.end) {
        updateSearch({ lastSeenStart: undefined, lastSeenEnd: undefined, page: 1 });
        return;
      }
      updateSearch({ lastSeenStart: iso.start, lastSeenEnd: iso.end, page: 1 });
    },
    [updateSearch]
  );

  const handleClearLastSeen = React.useCallback(
    () => updateSearch({ lastSeenStart: undefined, lastSeenEnd: undefined, page: 1 }),
    [updateSearch]
  );

  const handleResetFilters = React.useCallback(() => {
    updateSearch({
      domains: undefined,
      methods: undefined,
      statuses: undefined,
      credentialIds: undefined,
      lastSeenStart: undefined,
      lastSeenEnd: undefined,
      page: 1,
    });
  }, [updateSearch]);

  const emptyStateVariant = getApiCatalogEmptyStateVariant({
    hasItems: listItems.length > 0,
    isLoading: listQuery.isLoading,
    searchValue,
    hasActiveFilters,
  });

  const handleClearSearch = React.useCallback(() => {
    setLocalSearch('');
    updateSearch({ search: undefined, page: 1 });
  }, [updateSearch]);

  const searchLabel = searchValue.trim();

  const handleRowSelect = React.useCallback(
    (item: ApiCatalogListItem) => updateSearch({ requestId: item.id }),
    [updateSearch]
  );

  const handleDrawerOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) updateSearch({ requestId: undefined });
    },
    [updateSearch]
  );

  return (
    <DocsAnnotation slug="reference/api-catalog" title="API Catalog">
      <Page>
        <PageHeader
          icon={<Code size={24} />}
          title="API Catalog"
          description="Inspect observed API requests from crawler and workflow runs."
        />

        <div className="mt-6 space-y-6">
          <Flex align="center" gap="sm" wrap justify="between">
            {/* Source pills */}
            {sourcesQuery.data && sourcesQuery.data.length > 0 && (
              <Flex gap="xs" align="center" className="flex-shrink-0">
                {sourcesQuery.data.map((source) => {
                  const count = source.endpointCount ?? source.lastEndpointCount ?? 0;
                  return (
                    <Popover key={source.id} placement="bottom start">
                      <PopoverTrigger>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer"
                        >
                          <LinkSimple size={12} className="flex-shrink-0" />
                          <span className="truncate max-w-[120px]">{source.name}</span>
                          <span className="text-muted-foreground/60">{count}</span>
                          {source.lastSyncStatus === 'failed' && (
                            <span className="w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                          )}
                          {source.lastSyncStatus === 'syncing' && <Spinner size="sm" />}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0">
                        <div className="p-3 space-y-3">
                          <Flex align="center" justify="between">
                            <Text size="sm" weight="semibold" className="truncate flex-1">
                              {source.name}
                            </Text>
                            <Text size="xs" className="text-muted-foreground flex-shrink-0 ml-2">
                              {source.formatLabel}
                            </Text>
                          </Flex>

                          <div className="space-y-1.5 text-xs">
                            <Flex justify="between">
                              <Text size="xs" className="text-muted-foreground">
                                Endpoints
                              </Text>
                              <Text size="xs">{count}</Text>
                            </Flex>
                            <Flex justify="between">
                              <Text size="xs" className="text-muted-foreground">
                                Last synced
                              </Text>
                              <Text size="xs">
                                {source.lastSyncedAt
                                  ? formatRelativeTime(source.lastSyncedAt)
                                  : 'Never'}
                              </Text>
                            </Flex>
                            {source.lastSyncError && (
                              <Text size="xs" className="text-destructive mt-1">
                                {source.lastSyncError}
                              </Text>
                            )}
                          </div>

                          <div className="border-t pt-2">
                            <Flex gap="xs">
                              <Button
                                variant="secondary"
                                size="sm"
                                className="flex-1"
                                onPress={() => {
                                  refreshMutation.mutate(source.id, {
                                    onSuccess: (result) => {
                                      if (result.data) {
                                        const d = result.data;
                                        toast.success(
                                          `Refreshed: +${d.added} added, ~${d.updated} updated, -${d.removed} removed`
                                        );
                                      }
                                    },
                                    onError: (error) => {
                                      toast.error(
                                        error instanceof Error ? error.message : 'Refresh failed'
                                      );
                                    },
                                  });
                                }}
                                isDisabled={refreshMutation.isPending}
                                iconLeft={
                                  refreshMutation.isPending ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <ArrowsClockwise size={14} />
                                  )
                                }
                              >
                                Refresh
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onPress={() => {
                                  deleteSourceMutation.mutate(source.id, {
                                    onSuccess: (result) => {
                                      toast.success(
                                        `Removed source and ${result.data?.deletedEndpoints ?? 0} endpoint${(result.data?.deletedEndpoints ?? 0) !== 1 ? 's' : ''}`
                                      );
                                    },
                                    onError: (error) => {
                                      toast.error(
                                        error instanceof Error ? error.message : 'Delete failed'
                                      );
                                    },
                                  });
                                }}
                                isDisabled={deleteSourceMutation.isPending}
                                iconLeft={<Trash size={14} />}
                              >
                                Remove
                              </Button>
                            </Flex>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </Flex>
            )}

            <Flex gap="sm" align="center" className="flex-1 justify-end min-w-[200px]">
              <DialogTrigger
                isOpen={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                size="lg"
              >
                <Button variant="secondary" iconLeft={<Plus size={16} weight="bold" />}>
                  Add
                </Button>
                <CreateEndpointDialog onClose={() => setIsCreateDialogOpen(false)} />
              </DialogTrigger>
              <MenuTrigger>
                <Button variant="secondary" iconLeft={<UploadSimple size={16} weight="bold" />}>
                  Import
                </Button>
                <Menu>
                  <MenuItem id="url" onAction={() => setIsUrlImportDialogOpen(true)}>
                    <Flex align="center" gap="sm">
                      <LinkSimple size={16} />
                      From URL
                    </Flex>
                  </MenuItem>
                  <MenuItem id="postman" onAction={() => setIsImportDialogOpen(true)}>
                    <Flex align="center" gap="sm">
                      <UploadSimple size={16} />
                      From Postman file
                    </Flex>
                  </MenuItem>
                </Menu>
              </MenuTrigger>
              <DialogTrigger
                isOpen={isImportDialogOpen}
                onOpenChange={setIsImportDialogOpen}
                size="md"
              >
                <span className="hidden" />
                <ImportPostmanDialog onClose={() => setIsImportDialogOpen(false)} />
              </DialogTrigger>
              <DialogTrigger
                isOpen={isUrlImportDialogOpen}
                onOpenChange={setIsUrlImportDialogOpen}
                size="md"
              >
                <span className="hidden" />
                <ImportUrlDialog onClose={() => setIsUrlImportDialogOpen(false)} />
              </DialogTrigger>
              <DialogTrigger
                isOpen={isExportDialogOpen}
                onOpenChange={setIsExportDialogOpen}
                size="md"
              >
                <Button
                  variant="secondary"
                  iconLeft={<DownloadSimple size={16} weight="bold" />}
                  isDisabled={domainOptions.length === 0}
                >
                  Export
                </Button>
                <Dialog
                  title="Export to Postman"
                  description="Export API endpoints as a Postman collection."
                >
                  <DialogContent className="space-y-4">
                    <Select
                      label="Domain"
                      items={domainOptions}
                      selectedKey={exportDomain}
                      onSelectionChange={(key) => setExportDomain(key as string)}
                      placeholder="Select a domain..."
                    />
                    <Flex align="center" gap="sm">
                      <Checkbox
                        aria-label="Include response examples"
                        isSelected={exportIncludeExamples}
                        onChange={setExportIncludeExamples}
                      />
                      <Text>Include response examples</Text>
                    </Flex>
                    <Text size="xs" className="text-muted-foreground">
                      Sensitive headers (Authorization, Cookie, etc.) will be redacted.
                    </Text>
                  </DialogContent>
                  <DialogFooter>
                    <Button variant="secondary" onPress={() => setIsExportDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onPress={handleExport}
                      isDisabled={!exportDomain || exportMutation.isPending}
                    >
                      {exportMutation.isPending ? 'Exporting...' : 'Download Collection'}
                    </Button>
                  </DialogFooter>
                </Dialog>
              </DialogTrigger>
              <DialogTrigger
                isOpen={isColumnsDialogOpen}
                onOpenChange={setIsColumnsDialogOpen}
                size="md"
              >
                <Button variant="icon" iconOnly tooltip="Columns">
                  <SlidersHorizontal size={16} weight="bold" />
                </Button>
                <Dialog title="Table columns" description="Customize the API Catalog table.">
                  <DialogContent className="space-y-4">
                    <Flex align="center" gap="sm">
                      <Checkbox
                        aria-label="Show Domain column"
                        isSelected={showDomainColumn}
                        onChange={setShowDomainColumn}
                      />
                      <Text>Show Domain column</Text>
                    </Flex>
                    <Text size="xs" className="text-muted-foreground">
                      Domain is hidden by default because it is usually low-signal compared to Path.
                    </Text>
                  </DialogContent>
                  <DialogFooter>
                    <Button variant="primary" onPress={() => setIsColumnsDialogOpen(false)}>
                      Done
                    </Button>
                  </DialogFooter>
                </Dialog>
              </DialogTrigger>
            </Flex>
          </Flex>

          <ApiCatalogFilterBar
            searchInput={localSearch}
            onSearchChange={setLocalSearch}
            domainFilter={domainFilter}
            methodFilter={methodFilter}
            statusFilter={statusFilter}
            credentialFilter={credentialFilter}
            lastSeenRange={lastSeenRange}
            domainOptions={domainOptions}
            credentialOptions={credentialOptions}
            domainsLoading={domainMetadataQuery.isLoading}
            credentialsLoading={credentialsQuery.isLoading}
            hasActiveFilters={hasActiveFilters}
            onFilterChange={updateSearch}
            onLastSeenChange={handleLastSeenChange}
            onClearLastSeen={handleClearLastSeen}
            isMoreFiltersOpen={isMoreFiltersOpen}
            onMoreFiltersOpenChange={setIsMoreFiltersOpen}
          />
        </div>

        <BulkActionBar
          selectedCount={selectedCount}
          itemLabel="endpoint"
          onClearSelection={() => setRowSelection({})}
          actions={[
            {
              label: 'Delete',
              loadingLabel: 'Deleting…',
              variant: 'destructive',
              isLoading: bulkDeleteMutation.isPending,
              onPress: () => setIsDeleteConfirmOpen(true),
            },
          ]}
        />

        <Flex direction="column" gap="xl">
          {listQuery.isLoading ? (
            <Flex justify="center" className="py-24">
              <LoadingOrbit />
            </Flex>
          ) : emptyStateVariant === 'catalog-empty' ? (
            <Flex direction="column" align="center" gap="sm" className="py-24">
              <Text size="lg">No API requests yet</Text>
              <Text className="text-muted-foreground">
                Add an API source to import endpoints, or capture traces from crawler and workflow
                sessions.
              </Text>
              <Flex gap="sm" className="mt-4">
                <Button variant="primary" onPress={() => setIsUrlImportDialogOpen(true)}>
                  Add a Source
                </Button>
                <Button variant="secondary" onPress={() => setIsImportDialogOpen(true)}>
                  Import from Postman
                </Button>
                <Button variant="secondary" onPress={() => setIsCreateDialogOpen(true)}>
                  Add Endpoint
                </Button>
              </Flex>
            </Flex>
          ) : emptyStateVariant === 'no-results' ? (
            <Flex direction="column" align="center" gap="sm" className="py-24">
              <Text size="lg">No matching API requests</Text>
              <Text className="text-muted-foreground text-center max-w-xl">
                {searchLabel
                  ? `No endpoints match "${searchLabel}". Adjust the search or filters to see catalog results.`
                  : 'No endpoints match the current filters. Adjust the filters to see catalog results.'}
              </Text>
              <Flex gap="sm" className="mt-4">
                {searchLabel ? (
                  <Button variant="secondary" onPress={handleClearSearch}>
                    Clear search
                  </Button>
                ) : null}
                {hasActiveFilters ? (
                  <Button variant="secondary" onPress={handleResetFilters}>
                    Reset filters
                  </Button>
                ) : null}
              </Flex>
            </Flex>
          ) : (
            <div className={`transition-opacity ${listQuery.isFetching ? 'opacity-60' : ''}`}>
              <DataTable
                columns={tableColumns}
                data={listItems}
                getRowId={(item) => item.id}
                enableRowSelection
                rowSelection={rowSelection}
                onRowSelectionChange={(updater) =>
                  setRowSelection(typeof updater === 'function' ? updater(rowSelection) : updater)
                }
                onRowClick={handleRowSelect}
                serverPagination={
                  pagination
                    ? {
                        page: pagination.page,
                        pageSize: pagination.pageSize,
                        pageCount: pagination.totalPages,
                        totalItems: pagination.totalItems,
                        onPageChange: (nextPage) => updateSearch({ page: nextPage }),
                        isLoading: listQuery.isFetching,
                      }
                    : undefined
                }
              />
            </div>
          )}
        </Flex>

        <DrawerTrigger
          isOpen={drawerOpen}
          onOpenChange={handleDrawerOpenChange}
          allowWide
          defaultWide
        >
          <button style={{ display: 'none' }} />
          <Drawer hideCloseButton>
            <DrawerContent className="!p-0 h-full">
              <div className="drawer-detail-content h-full">
                {detailQuery.isLoading && !detailQuery.data ? (
                  <Flex justify="center" className="py-16">
                    <LoadingOrbit />
                  </Flex>
                ) : detailQuery.data?.data ? (
                  <ApiRequestDrawer detail={detailQuery.data.data} listItem={selectedListItem} />
                ) : selectedRequestId ? (
                  <Flex direction="column" align="center" gap="sm" className="py-16">
                    <Text size="lg">Failed to load request detail.</Text>
                    <Text className="text-muted-foreground">Please try again.</Text>
                  </Flex>
                ) : null}
              </div>
            </DrawerContent>
          </Drawer>
        </DrawerTrigger>

        <DialogTrigger isOpen={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
          <button style={{ display: 'none' }} />
          <Dialog
            title="Delete endpoints"
            description={`This will permanently delete ${selectedCount} endpoint${selectedCount === 1 ? '' : 's'} and all associated occurrences.`}
          >
            <DialogContent>
              <Text className="text-muted-foreground">This action cannot be undone.</Text>
            </DialogContent>
            <DialogFooter>
              <Button variant="secondary" onPress={() => setIsDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleBulkDelete}
                isDisabled={bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </Dialog>
        </DialogTrigger>
      </Page>
    </DocsAnnotation>
  );
};
